import { type FC, useEffect, useSyncExternalStore } from 'react';

import { observer } from 'mobx-react-lite';
import { Link, useLocation } from 'react-router-dom';

import {
  getCounterId,
  getServerCounterId,
  rememberReferrer,
  setAnalyticsEnabled,
  subscribeCounterId,
  trackGoal,
  trackPage,
} from '@shared/lib/analytics';
import { isRouteMetadataReady, subscribeRouteMetadata } from '@shared/lib/routeMetadata';

import styles from './AnalyticsConsent.module.css';
import { analyticsConsentStore } from '../model/store';

export const AnalyticsConsent: FC = observer(() => {
  const location = useLocation();
  const consent = analyticsConsentStore.consent;
  const counterId = useSyncExternalStore(subscribeCounterId, getCounterId, getServerCounterId);

  useEffect(() => {
    // cookie-boot уже скрыл сохранённый выбор до первого кадра; здесь — нажатие «Принять».
    // Разметка панели постоянна: чтение localStorage не расходится с пререндером при гидратации.
    if (consent === null) delete document.documentElement.dataset.analyticsConsent;
    else document.documentElement.dataset.analyticsConsent = consent;
  }, [consent]);

  useEffect(() => {
    rememberReferrer();
    // Согласие — продолжением пользования (уведомление «продолжая пользоваться…», решение владельца
    // 28.10.2026). Выключена аналитика только у тех, кто раньше явно отказался в прежнем уведомлении
    setAnalyticsEnabled(consent !== 'denied');
  }, [consent, counterId]);

  useEffect(() => {
    if (consent === 'denied') return;
    const recordPage = () => {
      if (isRouteMetadataReady(window.location.pathname)) {
        trackPage(window.location.href, document.title);
      }
    };
    const unsubscribe = subscribeRouteMetadata(recordPage);
    recordPage();
    return unsubscribe;
  }, [location.pathname, location.search, consent, counterId]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a');
      if (!link) return;
      const href = link.getAttribute('href') ?? '';
      if (href.startsWith('tel:')) trackGoal('phone_click');
      else if (href.startsWith('mailto:')) trackGoal('email_click');
      else if (
        link.origin === window.location.origin &&
        link.pathname === '/contacts' &&
        location.pathname !== '/contacts'
      )
        trackGoal('quote_request');
      else if (link.hostname === 'yandex.ru' && link.pathname.startsWith('/maps'))
        trackGoal('map_open');
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [location.pathname]);

  return (
    <section
      className={styles.panel}
      data-cookie-notice
      aria-label="Использование cookie"
      role="region"
    >
      <p className={styles.text}>
        Продолжая пользоваться сайтом, вы соглашаетесь на обработку{' '}
        <Link className={styles.policyLink} to="/cookies">
          файлов cookie
        </Link>
        , в том числе с использованием сервиса «Яндекс Метрика». Запретить обработку cookie можно в
        настройках браузера.
      </p>
      <div className={styles.actions}>
        <button
          className={styles.button}
          type="button"
          onClick={() => analyticsConsentStore.choose('granted')}
        >
          Принять
        </button>
      </div>
    </section>
  );
});
